import React from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Layout, Table, Button, Tag, Row, Col, message, DatePicker, Select, Space, Modal } from "antd";
import Search from "antd/es/input/Search";
import { PlusOutlined, DeleteOutlined, DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import { Header, AutoCompleteCompany } from "components";
import { RewardVoucherQrCodePrint } from "components/reward/RewardVoucherQrCodePrint";
import { getRewardBatches, cancelRewardBatch, exportRewardBatches } from "services/reward-batch-service";
import { getTotalBalance, getRewardBalance } from "services/company-service";
import { CardInfo } from "components/card/card-info";
import { formatMoney, formatDate, getMaskedCpf } from "utils";
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from "xlsx";
import { MONTHLY_REVENUE_RANGE_DATA } from "utils/static-data";

const { Content, Footer } = Layout;
const { RangePicker } = DatePicker;

const mapStateToProps = (state) => ({
  login: state.authReducer.loginReducer,
});

const RewardBatchScreen = (props) => {
  const { login } = props;
  const navigate = useNavigate();
  const [company, setCompany] = React.useState(login.company.master ? null : login.company.id);
  const [batches, setBatches] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [loading, setLoading] = React.useState(false);
  const today = new Date();
  const ninetyDaysAgo = new Date(today);
  ninetyDaysAgo.setDate(today.getDate() - 90);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [dateString, setDateString] = React.useState(`${fmt(ninetyDaysAgo)}:${fmt(today)}`);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [nameFilter, setNameFilter] = React.useState("");
  const [nameInput, setNameInput] = React.useState("");
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [companyBalance, setCompanyBalance] = React.useState(0);
  const [loadingBalance, setLoadingBalance] = React.useState(false);
  const [rewardBalance, setRewardBalance] = React.useState(0);
  const [loadingRewardBalance, setLoadingRewardBalance] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [qrVouchers, setQrVouchers] = React.useState([]);
  const [qrDataUrls, setQrDataUrls] = React.useState({});
  const [qrLoading, setQrLoading] = React.useState(false);
  const qrPrintRef = React.useRef();

  React.useEffect(() => {
    setPage(0);
    loadBatches(0, pageSize);
  }, [company, dateString, statusFilter, nameFilter, pageSize]);

  React.useEffect(() => {
    if (page > 0) loadBatches(page, pageSize);
  }, [page]);

  React.useEffect(() => {
    loadBalance();
    loadRewardBalance();
  }, [company]);

  const onGenerateQrCodes = async () => {
    setQrLoading(true);
    try {
      const res = await exportRewardBatches(selectedRowKeys);
      const actived = (res.vouchers || [])
        .filter((v) => v.status?.code === "actived")
        .map((v) => ({ ...v, _name: v.batchName }));
      if (actived.length === 0) {
        message.warning("Nenhum voucher ativo nos lotes selecionados.");
        return;
      }

      const QRCodeLib = (await import("qrcode")).default;
      const urls = {};
      await Promise.all(
        actived.map(async (v) => {
          urls[v.id] = await QRCodeLib.toDataURL(v.link, { margin: 1, width: 250 });
        })
      );
      setQrVouchers(actived);
      setQrDataUrls(urls);
      await new Promise((r) => setTimeout(r, 300));

      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const element = qrPrintRef.current;
      const pageEls = element.querySelectorAll("[data-pdf-page]");
      const pdf = new jsPDF("p", "mm", "a4");

      for (let i = 0; i < pageEls.length; i++) {
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(pageEls[i], { scale: 2, backgroundColor: "#ffffff", useCORS: true });
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, 210, 297);
      }

      pdf.save("qrcodes_lotes.pdf");
    } catch (e) {
      message.error("Erro ao gerar QR codes.");
    } finally {
      setQrLoading(false);
    }
  };

  const loadRewardBalance = async () => {
    if ((login.company.master && !company) || !company) return;
    setLoadingRewardBalance(true);
    try {
      const res = await getRewardBalance(login.company.master ? company : null);
      setRewardBalance(res?.balance ?? 0);
    } catch (e) {
      // silently ignore
    } finally {
      setLoadingRewardBalance(false);
    }
  };

  const loadBalance = async () => {
    if ((login.company.master && !company) || !company) return;
    setLoadingBalance(true);
    try {
      const res = await getTotalBalance(login.company.master ? company : null);
      setCompanyBalance(res.balance);
    } catch (e) {
      // silently ignore
    } finally {
      setLoadingBalance(false);
    }
  };

  const loadBatches = async (p = page, s = pageSize) => {
    if (login.company.master && !company) return;
    setLoading(true);
    try {
      const res = await getRewardBatches(
        login.company.master ? company : null,
        dateString,
        statusFilter,
        nameFilter || null,
        p,
        s
      );
      if (res.success) {
        setBatches(res.data || []);
        setTotal(res.total || 0);
      }
    } catch (e) {
      message.error("Erro ao carregar lotes");
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "—";
    const d = phone.replace(/\D/g, "").replace(/^55/, "").slice(0, 11);
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return phone;
  };

  const formatIncomeRange = (code) =>
    MONTHLY_REVENUE_RANGE_DATA.find((x) => x.code === code)?.name || code || "—";

  const formatGender = (code) =>
    code === "M" ? "Masculino" : code === "F" ? "Feminino" : "Prefiro não informar";

  const onExportExcel = async () => {
    setExportLoading(true);
    try {
      const res = await exportRewardBatches(selectedRowKeys);
      const rows = (res.vouchers || []).map((v) => {
        const rescued = v.status?.code === "rescued";
        return {
          "Nome do Lote": v.batchName || "",
          "E-mail": (rescued ? v.redeemerEmail : v.email) || v.email || "",
          Telefone: rescued
            ? (v.redeemerPhone ? formatPhone(v.redeemerPhone) : v.phone || "")
            : v.phone || "",
          Valor: formatMoney(v.amount, false),
          "Criado em": v.createdAt ? formatDate(v.createdAt) : "",
          "Expira em": v.expirationDate ? formatDate(v.expirationDate) : "",
          Link: v.link || "",
          Status: v.status?.name || "",
          Nome: v.redeemerName || "",
          CPF: v.redeemerCpf ? getMaskedCpf(v.redeemerCpf) : "",
          "E-mail Resgatador": v.redeemerEmail || "",
          "Telefone Resgatador": v.redeemerPhone ? formatPhone(v.redeemerPhone) : "",
          Sexo: rescued ? formatGender(v.redeemerGender) : "",
          "Data de Nascimento": rescued && v.redeemerBirthdate ? formatDate(v.redeemerBirthdate) : "",
          Ocupação: v.redeemerOccupation || "",
          "Faixa Salarial": formatIncomeRange(v.redeemerIncomeRange),
          CEP: v.redeemerAddressZip || "",
          Rua: v.redeemerAddressStreet || "",
          Número: v.redeemerAddressNumber || "",
          Complemento: v.redeemerAddressComplement || "",
          Bairro: v.redeemerAddressNeighborhood || "",
          Cidade: v.redeemerAddressCity || "",
          UF: v.redeemerAddressState || "",
          "Data do Resgate": v.rescuedAt ? formatDate(v.rescuedAt) : "",
          "Banco Receptor": v.pixBankName || "",
          "Titular da Chave": v.pixHolderName || "",
          "Doc. do Titular": v.pixHolderDocument ? getMaskedCpf(v.pixHolderDocument) : "",
          "Chave PIX": v.pixKey || "",
          "Tipo da Chave": v.pixKeyType || "",
          "PIX Iniciado em": v.pixInitiatedAt ? formatDate(v.pixInitiatedAt, "DD/MM/YYYY HH:mm") : "",
          "PIX Confirmado em": v.pixConfirmedAt ? formatDate(v.pixConfirmedAt, "DD/MM/YYYY HH:mm") : "",
        };
      });
      const wb = xlsxUtils.book_new();
      const ws = xlsxUtils.json_to_sheet(rows, { cellDates: true, dateNF: "YYYY-MM-DD" });
      xlsxUtils.book_append_sheet(wb, ws, "Vouchers");
      xlsxWriteFile(wb, "lotes_exportacao.xlsx");
    } catch (e) {
      message.error("Erro ao exportar relatório.");
    } finally {
      setExportLoading(false);
    }
  };

  const selectedActiveIds = React.useMemo(
    () => batches
      .filter((b) => selectedRowKeys.includes(b.id) && b.status?.code === "active")
      .map((b) => b.id),
    [batches, selectedRowKeys]
  );

  const onCancelSelected = () => {
    Modal.confirm({
      title: `Cancelar ${selectedActiveIds.length} lote(s)`,
      content:
        "Tem certeza? Os valores dos vouchers ativos serão devolvidos ao saldo da empresa.",
      okText: "Sim, cancelar",
      okButtonProps: { danger: true },
      cancelText: "Não",
      onOk: async () => {
        try {
          await Promise.all(selectedActiveIds.map((id) => cancelRewardBatch(id)));
          message.success(`${selectedActiveIds.length} lote(s) cancelado(s) com sucesso.`);
          setSelectedRowKeys([]);
          await loadBatches();
        } catch (e) {
          message.error("Erro ao cancelar lotes.");
        }
      },
    });
  };

  const statusTag = (status) => {
    const colorMap = {
      active: "green",
      cancelled: "red",
      finished: "default",
    };
    const color = colorMap[status?.code] ?? "default";
    return <Tag color={color}>{status?.name || "-"}</Tag>;
  };

  const columns = [
    {
      title: "Nome do Lote",
      dataIndex: "name",
      key: "name",
      align: "center",
    },
    {
      title: "Valor Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "center",
      render: (v) => formatMoney(v || 0),
    },
    {
      title: "Qtd. Prêmios",
      dataIndex: "totalVouchers",
      key: "totalVouchers",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (s) => statusTag(s),
    },
    {
      title: "Criado em",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (d) => (d ? formatDate(d, "DD/MM/YYYY HH:mm") : "-"),
    },
    {
      title: "Ações",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Button type="link" onClick={() => navigate(`/reward-voucher/${record.id}`)}>
          Ver detalhes
        </Button>
      ),
    },
  ];

  return (
    <Layout className="alymente-layout">
      <Header
        title="Link+"
        middle={
          login.company.master ? (
            <AutoCompleteCompany value={company} onChange={setCompany} />
          ) : undefined
        }
      />
      <Content style={{ padding: 16 }}>
        {(!login.company.master || company) && (
          <Row style={{ marginBottom: 16 }} gutter={16}>
            <Col>
              <CardInfo
                style={{ minWidth: 300, minHeight: 115 }}
                loading={loadingBalance}
                title="Saldo Atual"
                value={formatMoney(companyBalance)}
              />
            </Col>
            <Col>
              <CardInfo
                style={{ minWidth: 300, minHeight: 115 }}
                loading={loadingRewardBalance}
                title="Saldo Reservado (Link+)"
                value={formatMoney(rewardBalance)}
              />
            </Col>
          </Row>
        )}
        <Row gutter={[12, 8]} align="middle" style={{ marginBottom: 16, marginTop: 30 }}>
          <Col>
            <Search
              placeholder="Buscar por nome, e-mail, telefone ou valor"
              allowClear
              style={{ width: 240 }}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onSearch={(v) => { setNameInput(v); setNameFilter(v); }}
            />
          </Col>
          <Col>
            <RangePicker
              format="DD/MM/YYYY"
              allowClear
              onChange={(_, dates) => {
                if (!dates || !dates[0]) {
                  setDateString(null);
                } else {
                  const [d1, m1, y1] = dates[0].split("/");
                  const [d2, m2, y2] = dates[1].split("/");
                  setDateString(`${y1}-${m1}-${d1}:${y2}-${m2}-${d2}`);
                }
              }}
            />
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              onChange={setStatusFilter}
              options={[
                { value: "active", label: "Ativo" },
                { value: "cancelled", label: "Cancelado" },
                { value: "finished", label: "Encerrado" },
              ]}
            />
          </Col>
        </Row>
        <Table
          dataSource={batches}
          columns={columns}
          loading={loading}
          rowKey="id"
          rowSelection={{
            type: "checkbox",
            selectedRowKeys,
            onChange: exportLoading ? () => {} : setSelectedRowKeys,
          }}
          pagination={{
            current: page + 1,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50, 100],
            onChange: (p, s) => { setPageSize(s); setPage(s !== pageSize ? 0 : p - 1); },
          }}
          bordered
          size="small"
        />
      </Content>

      <Footer style={{ display: "flex", justifyContent: "space-between" }}>
        <Space>
          {!login.company.master && selectedActiveIds.length > 0 && (
            <Button danger icon={<DeleteOutlined />} onClick={onCancelSelected}>
              Cancelar Selecionados ({selectedActiveIds.length})
            </Button>
          )}
        </Space>
        <Space>
          {selectedRowKeys.length > 0 && (
            <Button icon={<FilePdfOutlined />} loading={qrLoading} onClick={onGenerateQrCodes}>
              Gerar QR Codes ({selectedRowKeys.length})
            </Button>
          )}
          <Button
            icon={<DownloadOutlined />}
            loading={exportLoading}
            disabled={selectedRowKeys.length === 0}
            onClick={onExportExcel}
          >
            Exportar Relatório {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
          </Button>
          {!login.company.master && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/reward-voucher/new")}
            >
              Criar Lote
            </Button>
          )}
        </Space>
      </Footer>
      <div style={{ position: "absolute", left: "-9999px", top: 0, opacity: 0, pointerEvents: "none" }}>
        <RewardVoucherQrCodePrint ref={qrPrintRef} vouchers={qrVouchers} qrDataUrls={qrDataUrls} />
      </div>
    </Layout>
  );
};

export default connect(mapStateToProps, {})(RewardBatchScreen);
